
import useSWR from "swr";
import { Modal, Select, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ChevronLeft, Loader, UploadCloud } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axiosConfig, { fetcher, handleMutation } from "../../../util/axiosConfig";
import { useCurrency } from "../../../hooks/useCurrency";
import { cn } from "../../../lib/utils";
import useSWRMutation from "swr/mutation";
import uploadToS3 from "../../../util/uploadToS3";
import TextArea from "antd/es/input/TextArea";

interface ReferralCommissionLedger {
  id: string;
  referralAgentId: string;
  rentalRequestId: string;
  paymentId: string | null;
  commissionRateId: string;
  transactionAmount: string | number;
  commissionType: string;
  commissionValue: string | number;
  commissionAmount: string | number;
  status: "EARNED" | "PENDING" | "PAID" | "VOIDED" | string;
  paidAt: string | null;
  paidBy: string | null;
  voidReason: string | null;
  createdAt: string;
  updatedAt: string;
  referralAgent?: {
    id: string;
    fullName: string;
    email: string;
    phone: string;
    referralCode: string;
  };
  rentalRequest?: {
    id: string;
    material: string;
    invoiceId: string | null;
    totalPrice: string | number;
    amountCharged: string | number;
    paymentMethod: string | null;
    renterName: string | null;
    status: string;
  };
  commissionRate?: {
    id: string;
    name: string;
    commissionType: string;
    commissionValue: string | number;
    isActive: boolean;
    effectiveFrom: string;
    effectiveTo: string | null;
  };
  paidByAdmin?: string | null;
}

interface ReferralCommissionLedgerResponse {
  success: boolean;
  data: ReferralCommissionLedger[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}


const paymentMethods = [
  {
    label: 'Transfer',
    value: 'transfer'
  },
  {
    label: 'Bank Deposit',
    value: 'deposit'
  },
]

function ReferralAgentTransaction({ agentId }: { agentId?: string }) {
  const params = useParams();
  const { formatCurrency } = useCurrency();
  const activeAgentId = agentId ?? params?.agentId ?? params?.referralAgentId;
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFromData] = useState({
    receiptImageUrl: '',
    paymentMethod: 'transfer',
    notes: ''
  });
  const [selectedLedger, setSelectedLedger] = useState<ReferralCommissionLedger | undefined>();
  const [pageSize, setPageSize] = useState(20);

  const query = activeAgentId
    ? `/referral/commission-ledgers?agentId=${activeAgentId}&page=${page}&limit=${pageSize}`
    : `/referral/commission-ledgers?page=${page}&limit=${pageSize}`;

  const { data, error, isLoading, mutate } = useSWR<ReferralCommissionLedgerResponse>(
    query,
    fetcher
  );

  const { trigger: processPayout, isMutating } = useSWRMutation(
    selectedLedger
      ? `/referral/mark-commission-paid/${selectedLedger.id}`
      : undefined,
    handleMutation
  );

  const ledgers = useMemo(() => data?.data ?? [], [data]);

  const handleProcessPayment = async () => {
    if (!selectedLedger || !formData.receiptImageUrl) return;
    try {
      const response = await processPayout({
       paymentReference: formData.receiptImageUrl,
       paymentMethod: formData.paymentMethod,
       notes: formData.notes
      });
      message.success(response.message || "Success");
      setIsModalOpen(false);
      mutate();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  }

  const voidCommission = async (ledgerId: string) => {
    try {
      await axiosConfig.post(`/referral/commission/${ledgerId}/void`);
      message.success("Commission voided");
      mutate();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Unable to void commission");
    }
  };

  const handleReceiptUpload = async (file: File) => {
    if (!file) return

    setIsUploading(true)
    try {
      await uploadToS3({
        file,
        data: {},
        filename: file.name,
        onSuccess: (url: string) => {
          setFromData({...formData, receiptImageUrl: url});
        },
        onError: (error: any) => {
          console.error("Upload failed:", error)
        },
      })
    } catch (error) {
      console.error("Upload error:", error)
    } finally {
      setIsUploading(false);
    }
  }  

  const columns: ColumnsType<ReferralCommissionLedger> = [
    {
      title: "Agent",
      dataIndex: "referralAgent",
      key: "referralAgent",
      render: (_, record) => (
        <div>
          <div className="font-medium text-primary capitalize">{record.referralAgent?.fullName || "N/A"}</div>
          <div className="text-xs text-gray-500">{record.referralAgent?.referralCode || "N/A"}</div>
        </div>
      ),
    },
    {
      title: "Material",
      dataIndex: "rentalRequest",
      key: "material",
      render: (_, record) => <span>{record.rentalRequest?.material || "N/A"}</span>,
    },
    {
      title: "Invoice",
      dataIndex: "rentalRequest",
      key: "invoice",
      render: (_, record) => <span>{record.rentalRequest?.invoiceId || "N/A"}</span>,
    },
    {
      title: "Transaction Amount",
      dataIndex: "transactionAmount",
      key: "transactionAmount",
      render: (_, record) => <span>{formatCurrency(Number(record.transactionAmount || 0))}</span>,
    },
    {
      title: "Commission",
      dataIndex: "commissionAmount",
      key: "commissionAmount",
      render: (_, record) => (
        <span>
          {record.commissionType === "PERCENTAGE"
            ? `${record.commissionValue}%`
            : formatCurrency(Number(record.commissionAmount || record.commissionValue || 0))}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => (
        <span className={cn(
          "font-semibold",
          record.status === "PAID" && "text-green-600",
          record.status === "VOIDED" && "text-red-500",
          record.status === "PENDING" && "text-amber-600",
          record.status === "EARNED" && "text-blue-600"
        )}>
          {record.status}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            className="text-white"
            disabled={record.status === "PAID" || record.status === "VOIDED"}
            onClick={() => {
              setSelectedLedger(record);
              setIsModalOpen(true);
            }}
          >
            Mark Paid
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="text-white"
            disabled={record.status === "VOIDED"}
            onClick={() => voidCommission(record.id)}
          >
            Void
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-md shadow-sm">
      <div className="flex items-center justify-between p-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" className="text-primary" onClick={() => window.history.back()}>
            <ChevronLeft size={20} />
          </Button>
          <h1 className="text-xl font-semibold text-primary">Referral Commission Ledger</h1>
        </div>
      </div>

      <div className="p-4">
        <Table
        columns={columns}
        dataSource={ledgers}
        rowKey="id"
        loading={isLoading}
        pagination={{
            current: data?.pagination?.page ?? page,
            pageSize: data?.pagination?.limit ?? pageSize,
            total: data?.pagination?.total ?? 0,
            onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize ?? 20);
          },
        }}
        />
      </div>
      <Modal
        title="Payment Details"
        okText="Confirm"
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isModalOpen}
        okButtonProps={{
          loading: isMutating
        }}
        onOk={handleProcessPayment}
        onCancel={() => setIsModalOpen(false)}
      >
        <>
          <div>
            <label htmlFor="receiptImageUrl" className="block text-sm font-medium text-gray-700 mb-1">
              Upload Receipt (optional)
            </label>
            <div className="relative flex justify-center items-center">
              <input
                type="file"
                id="receiptImageUrl"
                className="hidden"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    handleReceiptUpload(file)
                  }
                }}
              />
              <label
                htmlFor="receiptImageUrl"
                className={`flex items-center justify-center size-28 rounded border cursor-pointer ${formData.receiptImageUrl ? "bg-blue-100 border-blue-300" : "bg-blue-50 border-blue-100"
                  }`}
              >
              {formData.receiptImageUrl ? (
                  <img
                    src={formData.receiptImageUrl || "/placeholder.svg"}
                    alt="Receipt preview"
                    className="w-full h-full object-contain"
                  />
              ) : (<span className="flex items-center justify-center">
                  {isUploading
                  ? (<Loader size={30} className="animate-spin text-primary" />)
                  : <>
                    <UploadCloud size={50} className="text-primary"  />
                  </>}
                  </span>)}
              </label>
            </div>
          </div>
          <div className="mb-4">
            <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method
            </label>
            <Select
              id="paymentMethod"
              placeholder="Select payment method"
              value={formData.paymentMethod}
              onChange={(value) => {
                setFromData({...formData, paymentMethod: value});
              }}
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >=
                  0 ||
                option?.value?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              className="w-full"
            >
              {paymentMethods?.map((method) => {
                return (
                  <Select.Option value={method.value} key={method.value}>
                    {method.label}
                  </Select.Option>
                );
              })}
            </Select>
          </div>
          <div className="mb-4">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <TextArea
              id="notes"
              placeholder="Type notes"
              rows={3}
              value={formData.notes}
              onChange={(e) => setFromData({...formData, notes: e.target.value})}
            />
          </div>
        </>
        </Modal>
    </div>
  );
}

export default ReferralAgentTransaction;
